/**
 * Date fields travel as JSON (form definitions, saved form data), so a control value can
 * arrive as a Date, an ISO timestamp or a plain calendar day. MatDatepicker only accepts a
 * Date, and calendar days must not shift across timezones — hence the two helpers below.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Coerces a raw value into a Date for MatDatepicker; returns null when it is not a date. */
export function toDateValue(raw: unknown): Date | null {
  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'number') {
    const fromEpoch = new Date(raw);
    return isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  // A bare 'yyyy-MM-dd' is a calendar day: build it in local time so it keeps that exact day
  // (new Date('2026-07-29') would parse as UTC midnight and can render as the 28th).
  const dateOnly = DATE_ONLY.exec(raw.trim());
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Serializes a Date as a timezone-free calendar day ('yyyy-MM-dd'), for storage in form definitions. */
export function toDateOnlyString(value: unknown): string | null {
  const date = toDateValue(value);
  if (!date) {
    return null;
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Serializes a Date as a timezone-free local timestamp ('yyyy-MM-ddTHH:mm'). Deliberately not
 * toISOString(): that converts to UTC, which shifts the wall-clock time the user picked.
 */
export function toDateTimeString(value: unknown): string | null {
  const date = toDateValue(value);
  if (!date) {
    return null;
  }
  return `${toDateOnlyString(date)}T${toTimeString(date)}`;
}

/** The time part of a Date as 'HH:mm'. */
function toTimeString(value: unknown): string {
  const date = toDateValue(value);
  return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : '';
}

function pad(value: number): string {
  return `${value}`.padStart(2, '0');
}
